/**
 * v7.37 — Location Picker: clique no mapa pra definir lat/lng.
 *
 * Renderiza um Google Map interativo. Admin clica em qualquer ponto OU
 * arrasta o marker pra reposicionar. Coordenadas são propagadas via
 * `onChange({ lat, lng, address? })`.
 *
 * Props:
 *   - lat, lng       : posição inicial (opcional)
 *   - onChange({lat, lng, address})
 *   - height         : px (default 320)
 *   - defaultZoom    : default 14
 *
 * Fallback: se sem chave ou SDK falhar, renderiza mensagem amigável.
 * O admin ainda pode preencher coordenadas manualmente nos inputs
 * lat/lng do form pai.
 */
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps, isGoogleMapsConfigured } from '@/lib/googleMaps';
import { Loader2, AlertTriangle, MapPin } from 'lucide-react';

const DEFAULT_CENTER = { lat: -22.6128, lng: -43.1635 }; // Magé/Piabetá

export default function LocationPicker({
  lat,
  lng,
  onChange,
  height = 320,
  defaultZoom = 14,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const [status, setStatus] = useState('idle'); // idle | loading | ready | unavailable
  const [pickedAddress, setPickedAddress] = useState('');

  // Boot — só roda uma vez
  useEffect(() => {
    let cancelled = false;
    if (!isGoogleMapsConfigured()) {
      setStatus('unavailable');
      return;
    }
    setStatus('loading');
    loadGoogleMaps().then((ok) => {
      if (cancelled || !containerRef.current) return;
      if (!ok || !window.google?.maps) {
        setStatus('unavailable');
        return;
      }
      const startLat = Number(lat) || DEFAULT_CENTER.lat;
      const startLng = Number(lng) || DEFAULT_CENTER.lng;
      const map = new window.google.maps.Map(containerRef.current, {
        center: { lat: startLat, lng: startLng },
        zoom: defaultZoom,
        disableDefaultUI: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        gestureHandling: 'greedy',
      });
      const marker = new window.google.maps.Marker({
        position: { lat: startLat, lng: startLng },
        map,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
      });
      geocoderRef.current = new window.google.maps.Geocoder();

      const propagate = (latLng) => {
        const newLat = latLng.lat();
        const newLng = latLng.lng();
        // Reverse geocode em paralelo (não bloqueia o callback)
        geocoderRef.current?.geocode({ location: { lat: newLat, lng: newLng } }, (results, statusG) => {
          let addr = '';
          if (statusG === 'OK' && results && results[0]) {
            addr = results[0].formatted_address;
            setPickedAddress(addr);
          } else {
            setPickedAddress('');
          }
          onChangeRef.current?.({ lat: newLat, lng: newLng, address: addr });
        });
      };

      map.addListener('click', (e) => {
        marker.setPosition(e.latLng);
        propagate(e.latLng);
      });
      marker.addListener('dragend', (e) => {
        propagate(e.latLng);
      });

      mapRef.current = map;
      markerRef.current = marker;
      setStatus('ready');
    });
    return () => {
      cancelled = true;
      if (markerRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(markerRef.current);
      }
      if (mapRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(mapRef.current);
      }
      markerRef.current = null;
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync prop changes → marker + pan no mapa (quando parent atualiza lat/lng
  // externamente, ex: admin seleciona bairro no autocomplete acima).
  // v7.39 — Pan ATIVADO. Quando a posição vem de um click no próprio mapa,
  // o `panTo` para a coord onde o usuário acabou de clicar não causa
  // movimentação visível (o ponto já está na tela). Quando vem do
  // autocomplete, o mapa desliza suavemente até o novo bairro.
  useEffect(() => {
    if (status !== 'ready' || !markerRef.current || !mapRef.current) return;
    const numLat = Number(lat);
    const numLng = Number(lng);
    if (Number.isFinite(numLat) && Number.isFinite(numLng) && (numLat !== 0 || numLng !== 0)) {
      const newPos = { lat: numLat, lng: numLng };
      markerRef.current.setPosition(newPos);
      mapRef.current.panTo(newPos);
    }
  }, [lat, lng, status]);

  if (status === 'unavailable') {
    return (
      <div
        data-testid="location-picker-unavailable"
        className="flex items-center gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-800"
        style={{ minHeight: height }}
      >
        <AlertTriangle size={20} />
        <div className="text-sm">
          <p className="font-bold">Mapa indisponível</p>
          <p className="text-xs opacity-80">
            Defina <code>REACT_APP_GOOGLE_MAPS_KEY</code> no .env e habilite Maps JavaScript + Geocoding APIs no GCP.
            Você ainda pode digitar lat/lng manualmente abaixo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl overflow-hidden border-2 border-zinc-200" style={{ height }}>
        <div ref={containerRef} data-testid="location-picker-map" className="w-full h-full bg-zinc-100" />
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm">
            <Loader2 size={24} className="animate-spin text-zinc-500" />
            <span className="ml-2 text-sm text-zinc-600 font-bold">Carregando mapa…</span>
          </div>
        )}
      </div>
      {pickedAddress && (
        <div data-testid="location-picker-address" className="flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-900">
          <MapPin size={14} className="mt-0.5 shrink-0" />
          <span>{pickedAddress}</span>
        </div>
      )}
      <p className="text-[11px] text-zinc-500 leading-snug">
        💡 <strong>Clique no mapa</strong> ou <strong>arraste o marcador</strong> pra definir o centro do bairro. As coordenadas são preenchidas automaticamente.
      </p>
    </div>
  );
}
